/**
 * ChoiceBoardEditor Component
 * Editor for Choice Boards: a numbered list in board order. The tablet shows the first
 * CHOICE_BOARD_MAX_CHOICES (BA-87), so the editor caps adding there and mutes the rest (BA-92).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Paper,
  Typography,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  InfoOutlined as InfoIcon,
  KeyboardArrowDown as DownIcon,
  KeyboardArrowUp as UpIcon,
  VisibilityOff as HiddenIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CHOICE_BOARD_MAX_CHOICES, ScheduleStep } from '../../types/schedule';
import { Action } from '../../types/action';
import { scheduleService } from '../../services/scheduleService';

const BOARD_COLOR = '#9c27b0';
const MUTED = 'rgba(0,0,0,.26)';

const byOrder = (steps: ScheduleStep[]) => [...steps].sort((a, b) => a.order - b.order);

/** The choices the tablet shows: the first 3 by order (BA-87) */
export const shownChoices = (steps: ScheduleStep[]) => byOrder(steps).slice(0, CHOICE_BOARD_MAX_CHOICES);

interface ChoiceBoardNoticeProps {
  steps: ScheduleStep[];
  onShowChoices: () => void;
}

/** BA-92 E3: an older board with more than 3 choices. It can't be closed; it goes at 3 or fewer */
export const ChoiceBoardNotice: React.FC<ChoiceBoardNoticeProps> = ({ steps, onShowChoices }) => {
  const { t, i18n } = useTranslation();
  if (steps.length <= CHOICE_BOARD_MAX_CHOICES) return null;

  // Each title in bold, joined the language's way ("Lego, Drawing and Cars")
  const parts = new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' })
    .formatToParts(shownChoices(steps).map(s => s.title));
  const slot = '\u0001';
  const [before, after = ''] = (t('schedules.choiceBoard.notice.body', { count: steps.length, shown: slot }) as string)
    .split(slot);

  return (
    <Alert
      severity="warning"
      data-testid="choice-limit-notice"
      sx={{ mb: 3, flexWrap: { xs: 'wrap', sm: 'nowrap' }, '& .MuiAlert-message': { flex: { xs: '1 1 calc(100% - 40px)', sm: 1 } } }}
      action={
        <Button color="inherit" size="small" onClick={onShowChoices} sx={{ minHeight: 44 }}>
          {t('schedules.choiceBoard.notice.showChoices')}
        </Button>
      }
    >
      <AlertTitle>{t('schedules.choiceBoard.notice.title')}</AlertTitle>
      {before}
      {parts.map((part, i) => (part.type === 'element'
        ? <b key={i}>{part.value}</b>
        : <React.Fragment key={i}>{part.value}</React.Fragment>))}
      {after}
    </Alert>
  );
};

interface ChoiceBoardEditorProps {
  steps: ScheduleStep[];
  deviceActions: Action[];
  deviceId: string;
  scheduleId: string;
  onAddStep: () => void;
  onEditStep: (step: ScheduleStep) => void;
  onDeleteStep: (step: ScheduleStep) => void;
  /** A move failed; the list is back in the stored order */
  onReorderError: () => void;
}

export const ChoiceBoardEditor: React.FC<ChoiceBoardEditorProps> = ({
  steps,
  deviceActions,
  deviceId,
  scheduleId,
  onAddStep,
  onEditStep,
  onDeleteStep,
  onReorderError,
}) => {
  const { t } = useTranslation();

  // The order after the parent's taps, until the Firebase listener's steps catch up
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  const inFlight = useRef(false);
  const queued = useRef<string[] | null>(null);
  const latestSteps = useRef(steps);
  latestSteps.current = steps;

  const storedIds = (list: ScheduleStep[]) => byOrder(list).map(s => s.id).join();
  const stored = storedIds(steps);

  useEffect(() => {
    // The stored order (or the set of choices) changed: it wins once no move is pending
    if (!inFlight.current) setLocalOrder(null);
  }, [stored]);

  const sortedSteps = byOrder(steps);
  const displayed = localOrder
    ? [
        ...localOrder.map(id => sortedSteps.find(s => s.id === id)).filter((s): s is ScheduleStep => !!s),
        ...sortedSteps.filter(s => !localOrder.includes(s.id)),
      ]
    : sortedSteps;

  // m3: one reorder call at a time, and a tap during a call sends the latest full order after it
  const flush = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    let sent: string[] | null = null;
    try {
      while (queued.current) {
        sent = queued.current;
        queued.current = null;
        await scheduleService.reorderSteps(deviceId, scheduleId, sent);
      }
      inFlight.current = false;
      if (sent && storedIds(latestSteps.current) === sent.join()) setLocalOrder(null);
    } catch (err) {
      console.error('Failed to reorder choices:', err);
      inFlight.current = false;
      queued.current = null;
      setLocalOrder(null);
      onReorderError();
    }
  }, [deviceId, scheduleId, onReorderError]);

  const move = (index: number, by: -1 | 1) => {
    const ids = displayed.map(s => s.id);
    const to = index + by;
    if (to < 0 || to >= ids.length) return;
    [ids[index], ids[to]] = [ids[to], ids[index]];
    setLocalOrder(ids);
    queued.current = ids;
    flush();
  };

  const getActionForStep = (step: ScheduleStep) => {
    if (!step.actionId) return null;
    return deviceActions.find(a => a.id === step.actionId);
  };

  const count = steps.length;
  const canAdd = count < CHOICE_BOARD_MAX_CHOICES;

  const renderRow = (step: ScheduleStep, index: number) => {
    const shown = index < CHOICE_BOARD_MAX_CHOICES;
    const action = getActionForStep(step);
    const picture = action?.thumbnailUrl ?? action?.pictureUrl ?? undefined;
    const iconButton = (
      label: string,
      icon: React.ReactNode,
      onClick: () => void,
      disabled = false,
      color: 'default' | 'error' = 'default',
    ) => (
      <IconButton
        size="large"
        aria-label={label}
        disabled={disabled}
        color={color}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        sx={{ width: 48, height: 48, ...(color === 'default' && !disabled ? { color: 'action.active' } : {}) }}
      >
        {icon}
      </IconButton>
    );

    return (
      <Card
        key={step.id}
        data-testid="choice-row"
        data-shown={shown}
        aria-label={shown ? `${step.title}. ${t('schedules.choiceBoard.aria.position', { n: index + 1 })}` : undefined}
        onClick={() => onEditStep(step)}
        elevation={shown ? 1 : 0}
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          alignItems: 'center',
          columnGap: 1.5,
          rowGap: 1,
          minHeight: 80,
          py: 1,
          pr: 1,
          pl: 1.5,
          mb: 1,
          cursor: 'pointer',
          ...(shown ? {} : { bgcolor: 'grey.100', border: `1px dashed ${MUTED}` }),
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: shown ? BOARD_COLOR : MUTED,
            color: 'common.white',
            fontSize: 14,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          {index + 1}
        </Box>
        <Box
          {...(picture ? { component: 'img', src: picture, alt: action?.name } : {})}
          sx={{
            width: { xs: 56, sm: 64 },
            height: { xs: 56, sm: 64 },
            objectFit: 'cover',
            borderRadius: 2,
            flex: 'none',
            bgcolor: alpha(BOARD_COLOR, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2rem',
            ...(shown ? {} : { filter: 'grayscale(1)', opacity: 0.5 }),
          }}
        >
          {picture ? undefined : '🎯'}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body1"
            fontWeight={shown ? 500 : 400}
            color={shown ? 'text.primary' : 'text.secondary'}
            sx={{ overflowWrap: 'anywhere' }}
          >
            {step.title}
          </Typography>
          {!shown && (
            <Chip
              icon={<HiddenIcon />}
              label={t('schedules.choiceBoard.notShown')}
              size="small"
              variant="outlined"
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flex: { xs: '1 1 100%', sm: 'none' }, justifyContent: 'flex-end' }}>
          {iconButton(t('schedules.choiceBoard.aria.moveUp', { title: step.title }),
            <UpIcon sx={{ fontSize: 28 }} />, () => move(index, -1), index === 0)}
          {iconButton(t('schedules.choiceBoard.aria.moveDown', { title: step.title }),
            <DownIcon sx={{ fontSize: 28 }} />, () => move(index, 1), index === displayed.length - 1)}
          {iconButton(t('schedules.choiceBoard.aria.remove', { title: step.title }),
            <DeleteIcon />, () => onDeleteStep(step), false, 'error')}
        </Box>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('schedules.choiceBoard.instructions')}
      </Typography>

      <Paper
        sx={{
          p: { xs: 1, sm: 2 },
          bgcolor: alpha(BOARD_COLOR, 0.05),
          border: 2,
          borderColor: BOARD_COLOR,
        }}
      >
        {displayed.map((step, index) => (
          <React.Fragment key={step.id}>
            {index === CHOICE_BOARD_MAX_CHOICES && (
              <Box
                data-testid="choice-cutline"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  my: 1.5,
                  '&::before, &::after': { content: '""', flex: 1, height: '1px', bgcolor: MUTED },
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.4px' }}>
                  {t('schedules.choiceBoard.notShown')}
                </Typography>
              </Box>
            )}
            {renderRow(step, index)}
          </React.Fragment>
        ))}

        <Button
          fullWidth
          startIcon={<AddIcon />}
          onClick={onAddStep}
          disabled={!canAdd}
          aria-describedby={canAdd ? undefined : 'choice-limit-help'}
          sx={{
            mt: count > 0 ? 1 : 0,
            height: 56,
            border: '2px dashed',
            borderColor: alpha(BOARD_COLOR, 0.5),
            color: BOARD_COLOR,
            bgcolor: 'background.paper',
            '&.Mui-disabled': { borderColor: 'action.disabledBackground', bgcolor: 'transparent' },
          }}
        >
          {t('schedules.choiceBoard.addChoice')}
        </Button>
        {!canAdd && (
          <Typography
            id="choice-limit-help"
            variant="body2"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, mx: 0.25 }}
          >
            <InfoIcon sx={{ fontSize: 18 }} />
            {t('schedules.choiceBoard.limitHelp')}
          </Typography>
        )}
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
        {count > CHOICE_BOARD_MAX_CHOICES
          ? t('schedules.choiceBoard.overCount', { count })
          : t('schedules.choiceBoard.itemCount', { count })}
      </Typography>
    </Box>
  );
};

/** m7: the board is on the tablet now, which plays the copy it was sent */
export const ChoiceBoardActiveNote: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Alert severity="info" sx={{ mb: 3 }}>
      {t('schedules.choiceBoard.activeNote')}
    </Alert>
  );
};
